// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import ProductShareLink, { ProductShareLinkStyles } from "@bg/Overrides/Components/ProductShareLink";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, useDispatch } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProductContext & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
});

export interface ProductDetailsShareLinkStyles {
    wrapper?: InjectableCss;
    link?: ProductShareLinkStyles;
    printLink?: LinkPresentationProps;
}

export const shareLinkStyles: ProductDetailsShareLinkStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            z-index: 3;
        `,
    },
    link: {
        link: linkIconStyleGenerator("Share", "13px", 14, "margin-right: 20px"),
    },
    printLink: linkIconStyleGenerator("Print", "13px", 14),
};

const styles = shareLinkStyles;

const ProductDetailsShareLink: React.FC<Props> = ({ productContext: { product, productInfo } }) => {
    if (!product || !productInfo) {
        return null;
    }
    const dispatch = useDispatch();

    React.useEffect(() => {
        dispatch({
            type: "commonActionSetter/SetAction",
            payload: {
                name: "breadcrumbActions",
                value: (
                    <StyledWrapper {...styles.wrapper}>
                        <ProductShareLink
                            externalProduct={product}
                            externalProductInfo={productInfo}
                            extendedStyles={styles.link}
                        />
                        <Link {...styles.printLink} onClick={() => openPrintDialog()}>
                            {translate("Print")}
                        </Link>
                    </StyledWrapper>
                ),
            },
        });
        return () => {
            dispatch({
                type: "commonActionSetter/SetAction",
                payload: {
                    name: "breadcrumbActions",
                    value: null,
                },
            });
        };
    }, []);

    return <></>;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProductContext(ProductDetailsShareLink)),
    definition: {
        displayName: "Share Link",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
